class ApiClient {
  private baseUrl = __API_BASE_URL__ || ''
  private maxRetries = 3
  private retryableStatuses = new Set([502, 503, 504])

  private async fetchWithRetry(input: string, init?: RequestInit): Promise<Response> {
    let lastError: unknown
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const response = await fetch(input, init)
        if (this.retryableStatuses.has(response.status) && attempt < this.maxRetries) {
          await this.delay(attempt)
          continue
        }
        return response
      } catch (error) {
        lastError = error
        if (attempt < this.maxRetries) {
          await this.delay(attempt)
          continue
        }
      }
    }
    throw lastError
  }

  private delay(attempt: number): Promise<void> {
    const ms = Math.min(1000 * 2 ** attempt, 8000)
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  private handleUnauthorized(response: Response, url: string) {
    if (response.status === 401 && !url.endsWith('/api/auth/user')) {
      window.dispatchEvent(new Event('auth-expired'))
    }
  }

  async get<T>(url: string): Promise<T> {
    const response = await this.fetchWithRetry(this.baseUrl + url, {
      credentials: 'include',
    })
    
    if (!response.ok) {
      this.handleUnauthorized(response, url)
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const text = await response.text()
    return text ? JSON.parse(text) : undefined as T
  }

  async post<T = void>(url: string, data?: unknown): Promise<T> {
    const response = await this.fetchWithRetry(this.baseUrl + url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    })
    
    if (!response.ok) {
      this.handleUnauthorized(response, url)
      const errorText = await response.text()
      let errorMessage = `HTTP error! status: ${response.status}`
      if (errorText) {
        try {
          const parsed = JSON.parse(errorText)
          errorMessage = parsed.message || errorText
        } catch {
          errorMessage = errorText
        }
      }
      throw new Error(errorMessage)
    }
    
    if (response.status === 204) {
      return undefined as T
    }
    
    const text = await response.text()
    return text ? JSON.parse(text) : undefined as T
  }

  async delete<T = void>(url: string): Promise<T> {
    const response = await this.fetchWithRetry(this.baseUrl + url, {
      method: 'DELETE',
      credentials: 'include',
    })
    
    if (!response.ok) {
      this.handleUnauthorized(response, url)
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    if (response.status === 204) {
      return undefined as T
    }
    
    return response.json()
  }

  async put<T = void>(url: string, data?: unknown): Promise<T> {
    const response = await this.fetchWithRetry(this.baseUrl + url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    })
    
    if (!response.ok) {
      this.handleUnauthorized(response, url)
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    if (response.status === 204) {
      return undefined as T
    }
    
    return response.json()
  }
  async uploadMedia(file: File): Promise<{ id: string; url: string; fileName: string; contentType: string }> {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await fetch(this.baseUrl + '/api/uploads', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
    
    if (!response.ok) {
      this.handleUnauthorized(response, '/api/uploads')
      const errorText = await response.text()
      let errorMessage = `Upload failed: ${response.status}`
      if (errorText) {
        try {
          const parsed = JSON.parse(errorText)
          errorMessage = parsed.message || errorText
        } catch {
          errorMessage = errorText
        }
      }
      throw new Error(errorMessage)
    }
    
    return response.json()
  }
}

export const apiClient = new ApiClient()
